import { Injectable, inject } from '@angular/core';
import { Observable, map, of, switchMap, tap } from 'rxjs';
import { CompanyFlags, CompanyContextService } from './company-context.service';
import { LookupsService } from './lookups.service';
import { CompaniesService } from '../../features/companies/data/companies.service';
import { CompanyProfileDto } from '../../features/companies/models/company.models';

/**
 * Seçili şirketin ülke/locale/e-fatura bayraklarını API profilinden çözer ve
 * CompanyContextService'e yazar. API yeni alanları (countryIso2, localeCode,
 * supportsEInvoicing) döndürmüyorsa ülke lookup'ı üzerinden geriye dönük çözer.
 */
@Injectable({ providedIn: 'root' })
export class CompanyFlagsService {
  private readonly companiesService = inject(CompaniesService);
  private readonly lookups = inject(LookupsService);
  private readonly companyContext = inject(CompanyContextService);

  resolveAndStore(companyId: string): Observable<CompanyFlags> {
    return this.companiesService.getByPublicId(companyId).pipe(
      switchMap((profile) => this.toFlags(profile)),
      tap((flags) => this.companyContext.setCompanyFlags(flags)),
    );
  }

  private toFlags(profile: CompanyProfileDto): Observable<CompanyFlags> {
    return this.resolveBaseCurrencyCode(profile).pipe(
      switchMap((baseCurrencyCode) => {
        if (profile.countryIso2 != null || profile.supportsEInvoicing !== undefined) {
          return of({
            countryIso2: profile.countryIso2 ?? null,
            countryId: profile.countryId ?? null,
            localeCode: profile.localeCode ?? null,
            supportsEInvoicing: profile.supportsEInvoicing ?? null,
            baseCurrencyCode,
            isVatRegistered: profile.isVatRegistered ?? null,
            features: profile.entitledFeatures ?? null,
            packageKey: profile.packageKey ?? null,
          });
        }

        return this.lookups.getCountries().pipe(
          map((countries) => {
            const country = countries.find((c) => c.id === profile.countryId);
            return {
              countryIso2: country?.code ?? null,
              countryId: profile.countryId ?? null,
              localeCode: null,
              supportsEInvoicing: country ? country.code === 'TR' : null,
              baseCurrencyCode,
              isVatRegistered: profile.isVatRegistered ?? null,
              features: profile.entitledFeatures ?? null,
              packageKey: profile.packageKey ?? null,
            };
          }),
        );
      }),
    );
  }

  /** Muhasebe (base) para birimi; API baseCurrencyId döndürmüyorsa defaultCurrencyId'ye düşer. */
  private resolveBaseCurrencyCode(profile: CompanyProfileDto): Observable<string | null> {
    const currencyId = profile.baseCurrencyId ?? profile.defaultCurrencyId;
    if (currencyId == null) return of(null);
    return this.lookups.getCurrencies().pipe(
      map((currencies) => currencies.find((c) => c.id === currencyId)?.code ?? null),
    );
  }
}
